import qs from 'qs'
import Vue from 'vue'
import Router from 'vue-router'
import store from '../store'
import { Login, Logout, ForgotPassword, PasswordResetToken, SignUp } from '@/views/login'
import { Admin, Users, Account, RulesWhitelist, Preferences, Allowlist, AllowlistWorkflow } from '@/views/admin'
import {
  Integrations,
  IntegrationConfiguration,
  IntegrationWorkflow,
  EditIntegrationWorkflow
} from '@/views/integrations'
import {
  HomeDashboard,
  ParentSecurityPostureDashboard,
  TrailsSecurityPostureDashboard,
  AgentsSecurityPostureDashboard,
  PrivilegedSecurityPostureDashboard
} from '@/views/dashboard'
import {
  Monitor,
  BaseCyberAttacks,
  CyberAttacks,
  CyberAttackTrailDetails,
  CyberAttackTrailGraph,
  PrivilegedActivities,
  MitigatedCyberAttacks,
  QuarantinedFiles,
  NetworkRestrictions,
  TrailsInfoFingerprint,
  AccountKeyProgressions
} from '@/views/monitor'
import {
  AssetView,
  Hosts,
  HostDashboard,
  HostDetails,
  HostInsights,
  ContainerDetails,
  ContainerListView,
  ContainerImagesListView,
  AccountKeyHosts,
  AccountKeyContainerListView
} from '@/views/hosts'
import { Detections, PrivilegedDetections, TrailDetections, AccountKeyDetections } from '@/views/detections'
import { Rules, PrivilegedRules } from '@/views/rules'
import Activation from '@/views/activation/Activation'
import {
  Insights,
  RenderInsights,
  DnsRequestInsights,
  LogonFailureInsights,
  ExternalConnectionInsights,
  PrivilegedExecutionInsights
} from '@/views/insights'
import RuleView from '@/views/rules/RuleView'
import { InstallationLanding, InstallationNetworkReq, InstallationAgent } from '@/views/installation'
import MitreAttackCoverage from '@/views/mitreattack/MitreAttackCoverage'
import ThreatHunting from '@/views/threat-hunting/ThreatHunting'
import { AnomaliesView, Anomalies, AnomalyRules } from '@/views/anomalies'
import { ReportsView, ReportsTemplate, ReportsScheduled } from '@/views/reports'
import { AlertIntegrationAccounts, DiscoveryAccounts } from '@/views/integration-accounts'

import { PREV_ROUTE } from '@/store/modules/auth/auth.type'

if (!process || process.env.NODE_ENV !== 'testing') {
  Vue.use(Router)
}

const ifAuthenticated = (to, from, next) => {
  /**
   * to.meta.resource carries Permission resource for each veiw
   */

  // if request is for non-auth page (login, forget-password, sign-up)
  // clear user session and forward to page
  if (_.indexOf(['login', 'forgot-password', 'reset-password', 'signup'], to.name) > -1) {
    // to detect if user is coming from Identity provider
    if (!_.isEmpty(to.query)) {
      next()
    } else if (!_.isEmpty(localStorage.getItem('user_token'))) {
      // if user session persist, logout and clear local storage
      next('/logout')
    } else {
      next()
    }
  } else if (!_.isEmpty(localStorage.getItem('user_token'))) {
    // proceed if user_token is present
    // check if user has permission else route back to dashboard
    let user = store.getters['userProfile']
    let permissions = _.flatten(_.map(user.roles, 'permissions'))
    if (_.some(permissions, { key: to.meta.resource })) {
      next()
    } else {
      // cancel navigation
      next(false)
    }
  } else {
    if (to.name === 'attack-track-details' || to.name === 'account') {
      // if user isn't logged in and trying to access
      // attack details or accounts page via email
      store.commit(PREV_ROUTE, to)
    }
    next('/login')
  }
}

const router = new Router({
  parseQuery (query) {
    if (query.includes('search=')) {
      let parsedQuery = qs.parse(query)
      parsedQuery.search = parsedQuery.search.replace(/\s/g, '+')
      return parsedQuery
    }
    return qs.parse(query)
  },
  stringifyQuery (query) {
    if (!_.isEmpty(query.search)) {
      query.search = query.search.replace(/\s/g, '+')
    }
    const result = qs.stringify(query)
    return result ? `?${decodeURIComponent(result)}` : ''
  },
  routes: [
    {
      path: '/login',
      name: 'login',
      component: Login,
      beforeEnter: ifAuthenticated
    },
    {
      path: '/activation',
      name: 'activation',
      beforeEnter: ifAuthenticated,
      component: Activation,
      meta: {
        auth: true,
        resource: 'network'
      }
    },
    {
      path: '/home',
      name: 'dashboard-analytics',
      component: HomeDashboard,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/dashboard/trails',
      name: 'dashboard',
      component: ParentSecurityPostureDashboard,
      alias: '/dashboard',
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      },
      children: [
        {
          path: 'trails',
          name: 'dashboard-trails',
          component: TrailsSecurityPostureDashboard,
          props: route => ({
            assignee: route.query.assignee
          }),
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'cyber_attacks'
          }
        },
        {
          path: 'privileged',
          name: 'dashboard-privileged',
          component: PrivilegedSecurityPostureDashboard,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'cyber_attacks'
          }
        },
        {
          path: 'agents',
          name: 'dashboard-agents',
          component: AgentsSecurityPostureDashboard,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'cyber_attacks'
          }
        }
      ]
    },
    {
      path: '/monitor',
      name: 'monitor',
      redirect: '/monitor/cyber-attacks',
      component: Monitor,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      },
      children: [
        {
          path: 'cyber-attacks',
          component: BaseCyberAttacks,
          children: [
            {
              path: 'active',
              name: 'active-cyber-attacks',
              component: CyberAttacks,
              beforeEnter: ifAuthenticated,
              meta: {
                auth: true,
                resource: 'cyber_attacks'
              }
            },
            {
              path: 'mitigated',
              name: 'mitigated-cyber-attacks',
              component: MitigatedCyberAttacks,
              beforeEnter: ifAuthenticated,
              meta: {
                auth: true,
                resource: 'cyber_attacks'
              }
            }
          ]
        },
        {
          path: 'account-progressions',
          name: 'account-progressions',
          component: AccountKeyProgressions,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'cyber_attacks'
          }
        },
        {
          path: 'privileged-activities',
          name: 'privileged-activities',
          component: PrivilegedActivities,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'cyber_attacks'
          }
        },
        {
          path: 'details/:trailId',
          name: 'attack-track-details',
          component: CyberAttackTrailDetails,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'cyber_attacks'
          }
        },
        {
          path: 'details/graph/:platform/:trailId', // :trailId',
          name: 'attack-track-graph',
          component: CyberAttackTrailGraph,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'cyber_attacks'
          }
        },
        {
          path: 'rules',
          name: 'rules',
          redirect: '/monitor/rules/attack',
          component: RuleView,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'rules'
          },
          children: [
            {
              path: 'attack',
              name: 'attack-rules',
              component: Rules,
              beforeEnter: ifAuthenticated,
              meta: {
                auth: true,
                resource: 'rules'
              }
            },
            {
              path: 'privileged',
              name: 'privileged-rules',
              component: PrivilegedRules,
              beforeEnter: ifAuthenticated,
              meta: {
                auth: true,
                resource: 'rules'
              }
            }
          ]
        }
      ]
    },
    {
      path: '/quarantined-files',
      name: 'quarantined-files',
      component: QuarantinedFiles,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/network-restrictions',
      name: 'network-restrictions',
      component: NetworkRestrictions,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/rules/whitelist',
      name: 'rules-whitelist',
      component: RulesWhitelist,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'rules'
      }
    },
    {
      path: '/rules/allowlist',
      name: 'allowlist',
      component: Allowlist,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'rules'
      }
    },
    {
      path: '/rules/allowlist/create',
      name: 'create-allowlist',
      component: AllowlistWorkflow,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'rules'
      }
    },
    {
      path: '/rules/allowlist/edit',
      name: 'edit-allowlist',
      component: AllowlistWorkflow,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'rules'
      }
    },
    {
      path: '/rules/allowlist/duplicate',
      name: 'duplicate-allowlist',
      component: AllowlistWorkflow,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'rules'
      }
    },
    {
      path: '/rules/allowlist/detection',
      name: 'detection-allowlist',
      component: AllowlistWorkflow,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'rules'
      }
    },
    {
      path: '/preferences',
      name: 'preferences',
      beforeEnter: ifAuthenticated,
      component: Preferences,
      meta: {
        auth: true,
        resource: 'users'
      }
    },
    {
      path: '/integrations',
      name: 'integrations',
      beforeEnter: ifAuthenticated,
      component: Integrations,
      meta: {
        auth: true,
        resource: 'preferences'
      }
    },
    {
      path: '/integrations/accounts/discovery',
      name: 'discoveryintegration-accounts',
      beforeEnter: ifAuthenticated,
      component: DiscoveryAccounts,
      meta: {
        auth: true,
        resource: 'preferences'
      }
    },
    {
      path: '/integrations/accounts/alert',
      name: 'alertintegration-accounts',
      beforeEnter: ifAuthenticated,
      component: AlertIntegrationAccounts,
      meta: {
        auth: true,
        resource: 'preferences'
      }
    },
    {
      path: '/integrations/configuration',
      name: 'integration-configuration',
      beforeEnter: ifAuthenticated,
      component: IntegrationConfiguration,
      meta: {
        auth: true,
        resource: 'preferences'
      }
    },
    {
      path: '/integrations/configuration/:integrationType',
      name: 'integration-workflow',
      component: IntegrationWorkflow,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'agents'
      }
    },
    {
      path: '/integrations/configuration/edit/:integrationType/:accountKey',
      name: 'edit-integration-workflow',
      component: EditIntegrationWorkflow,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'agents'
      }
    },
    {
      path: '/admin',
      name: 'admin',
      redirect: '/admin/account',
      beforeEnter: ifAuthenticated,
      component: Admin,
      meta: {
        auth: true,
        resource: 'users'
      },
      children: [
        {
          path: 'account',
          name: 'account',
          component: Account,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'users'
          }
        },
        {
          path: 'users',
          name: 'users',
          component: Users,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'users'
          }
        }
      ]
    },
    {
      path: '/assets',
      name: 'assets',
      redirect: '/assets/hosts',
      component: AssetView,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'rules'
      },
      children: [
        {
          path: 'hosts',
          name: 'hosts',
          component: Hosts,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'agents'
          }
        },
        {
          path: 'containers',
          name: 'containers',
          component: ContainerListView,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'agents'
          }
        },
        {
          path: 'container-images',
          name: 'container-images',
          component: ContainerImagesListView,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'agents'
          }
        }
      ]
    },
    {
      path: '/assets/hosts/account-hosts',
      name: 'account-hosts',
      component: AccountKeyHosts,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/assets/containers/account-containers',
      name: 'account-containers',
      component: AccountKeyContainerListView,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/hosts/dashboard',
      name: 'dashboard-hosts',
      component: HostDashboard,
      children: [
        {
          path: 'insights/:agentId',
          name: 'host-insights',
          component: HostInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'agents'
          }
        },
        {
          path: 'details/:agentId',
          name: 'host-details',
          component: HostDetails,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'agents'
          }
        },
        {
          path: 'container/:agentId',
          name: 'container-details',
          component: ContainerDetails,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'agents'
          }
        }
      ]
    },
    {
      path: '/trail-fingerprint/:fingerprintId',
      name: 'trail-fingerprint',
      component: TrailsInfoFingerprint,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/trail-iocs',
      name: 'trail-iocs',
      component: TrailDetections,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/account-iocs',
      name: 'account-iocs',
      component: AccountKeyDetections,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/detections',
      name: 'detections',
      component: Detections,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/detections/privileged',
      name: 'privileged-detections',
      component: PrivilegedDetections,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/insights',
      name: 'insights',
      redirect: '/insights/windows-dns-requests',
      component: Insights,
      meta: {
        auth: true,
        resource: 'statistics'
      },
      children: [
        {
          path: 'windows-dns-requests',
          name: 'windows-dns-requests-insights',
          component: DnsRequestInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics'
          }
        },
        {
          path: 'windows-external-connections',
          name: 'windows-external-connections-insights',
          component: ExternalConnectionInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics'
          }
        },
        {
          path: 'windows-logon-failures',
          name: 'windows-logon-failures-insights',
          component: LogonFailureInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics'
          }
        },
        {
          path: 'windows-privileged-executions',
          name: 'windows-privileged-executions-insights',
          component: PrivilegedExecutionInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics'
          }
        },
        {
          path: 'linux-process-launching-shells',
          name: 'linux-process-launching-shells-insights',
          component: RenderInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics',
            os: 'linux',
            name: 'process-launching-shells',
            title: 'Process Launching Shells',
            group: 'parentComm',
            statsCols: ['Program', 'Count']
          }
        },
        {
          path: 'linux-privileged-executions',
          name: 'linux-privileged-executions-insights',
          component: RenderInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics',
            os: 'linux',
            name: 'privileged-execution',
            title: 'Linux Privileged Executions',
            group: 'sourceBinaryName',
            statsCols: ['Program', 'Count']
          }
        },
        {
          path: 'linux-login-failures',
          name: 'linux-login-failures-insights',
          component: RenderInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics',
            os: 'linux',
            name: 'login-failures',
            title: 'Linux Login Failures',
            group: 'argsAddr',
            statsCols: ['IP', 'Count']
          }
        },
        {
          path: 'linux-sensitive-file-access',
          name: 'linux-sensitive-file-access-insights',
          component: RenderInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics',
            os: 'linux',
            name: 'sensitive-file-access',
            title: 'Linux Sensitive File Access',
            group: 'sourceBinaryName',
            statsCols: ['Program', 'Count']
          }
        },
        {
          path: 'linux-external-connections',
          name: 'linux-external-connections-insights',
          component: RenderInsights,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics',
            os: 'linux',
            name: 'external/connects',
            title: 'Linux External Connections',
            group: 'sourceComm',
            statsCols: ['Program', 'Count']
          }
        }
      ]
    },
    {
      path: '/anomalies',
      name: 'anomalies-parent',
      redirect: '/anomalies/detections',
      component: AnomaliesView,
      meta: {
        auth: true,
        resource: 'statistics'
      },
      children: [
        {
          path: 'detections',
          name: 'anomalies-detections',
          component: Anomalies,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics'
          }
        },
        {
          path: 'rules',
          name: 'anomalies-rules',
          component: AnomalyRules,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics'
          }
        }
      ]
    },
    {
      path: '/reports',
      name: 'reports-parent',
      redirect: '/reports/templates',
      component: ReportsView,
      meta: {
        auth: true,
        resource: 'statistics'
      },
      children: [
        {
          path: 'templates',
          name: 'reports-templates',
          component: ReportsTemplate,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics'
          }
        },
        {
          path: 'scheduled',
          name: 'reports-scheduled',
          component: ReportsScheduled,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'statistics'
          }
        }
      ]
    },
    {
      path: '/installation',
      name: 'installation',
      redirect: '/installation/agent',
      beforeEnter: ifAuthenticated,
      component: InstallationLanding,
      meta: {
        auth: true,
        resource: 'network'
      },
      children: [
        {
          path: 'network',
          name: 'network-req',
          component: InstallationNetworkReq,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'network'
          }
        },
        {
          path: 'agent',
          name: 'install-agent',
          component: InstallationAgent,
          beforeEnter: ifAuthenticated,
          meta: {
            auth: true,
            resource: 'network'
          }
        }
      ]
    },
    {
      path: '/threat-hunting',
      name: 'threat-hunting',
      component: ThreatHunting,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'cyber_attacks'
      }
    },
    {
      path: '/mitre-coverage',
      name: 'mitre-coverage',
      component: MitreAttackCoverage,
      beforeEnter: ifAuthenticated,
      meta: {
        auth: true,
        resource: 'rules'
      }
    },
    {
      path: '/logout',
      name: 'logout',
      component: Logout
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPassword,
      beforeEnter: ifAuthenticated
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: PasswordResetToken,
      beforeEnter: ifAuthenticated
    },
    {
      path: '/signup-token/:email/:token',
      name: 'signup',
      component: SignUp,
      beforeEnter: ifAuthenticated
    },
    {
      path: '*',
      redirect: {
        name: 'dashboard-analytics'
      },
      beforeEnter: ifAuthenticated
    }
  ]
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js